import {isAfter, isBefore, parseISO} from 'date-fns'
import {
    filters as filtersBlank,
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '@/utils/cars/cars-rent-blank-state'
import {UTCToDate} from '@/utils/helpers'
import {salesTermsRsTransformer} from '@/utils/api-helpers'
import {appInstance} from '@/utils/app-accessor'
import {carsRentRuntimeStore, runtimeStore} from '@/composables/store-accessor'
import type {Ref} from 'vue'
import {useProductStore} from '~/composables/product-store'

function getBasketItem(basket: Ref<any[]>, offerKey: any) {
    return (
        basket.value.find((item: {offerKey: any}) => item.offerKey === offerKey) || {
            searchRequest: searchRequestBlank(),
            product: {offer: {}, info: {}},
        }
    )
}

export const useCarsRentStore = defineStore('carsRent', () => {
    const searchRequest: Ref = ref(searchRequestBlank())
    const searchResponse: Ref = ref(searchResponseBlank())
    const filters: Ref = ref(filtersBlank())
    const sortFnName: Ref = ref('priceAsc')
    const searchExpirationTime: Ref = ref(null)

    const prepareBookRequest: Ref = ref({})
    const prepareBookResponse: Ref = ref({})
    const basket: Ref<any[]> = ref([])
    const bookingAdditionalOptions: Ref<any[]> = ref([])
    const pickUpLocation: Ref = ref({})
    const dropOffLocation: Ref = ref({})

    function ADD_TO_BASKET({offerKey}: any) {
        basket.value = basket.value.filter(item => {
            const offer = item.product.offer
            return isBefore(new Date(), UTCToDate(parseISO(offer.expirationTime)))
        })
        if (getBasketItem(basket, offerKey).offerKey) return

        const product = carsRentRuntimeStore.offers.find((product: any) => product.offer.offerKey === offerKey)

        basket.value.push({
            offerKey,
            product,
            searchRequest: searchRequest.value,
        })
    }

    function REFRESH_BASKET_PRICE({offerKey, prepareBookResponse}: any) {
        const basketItem = getBasketItem(basket, offerKey)
        if (!basketItem.offerKey) return
        const offer = basketItem.product.offer
        const {price} = salesTermsRsTransformer(prepareBookResponse.currentSalesTerms)
        offer.currentPrice = price
    }

    function SET_SEARCH_REQUEST(rq: any) {
        searchRequest.value = rq
    }

    function RESET_FILTERS() {
        filters.value = filtersBlank()
    }

    function SET_FILTER({key, value}: any) {
        filters.value[key] = value
    }

    function SET_FILTERS(val: any) {
        filters.value = val
    }

    function SET_SORT(sort: any) {
        sortFnName.value = sort
    }

    function NEW_SEARCH(_searchRequest: any) {
        searchRequest.value = _searchRequest
        filters.value = filtersBlank()
    }

    function RESET() {
        searchRequest.value = searchRequestBlank()
        pickUpLocation.value = {}
        dropOffLocation.value = {}
        prepareBookRequest.value = {}
    }

    function SET_PREPARE_BOOK_RESPONSE(_prepareBookResponse: any) {
        prepareBookResponse.value = _prepareBookResponse
    }

    function SET_SEARCH_EXPIRATION_TIME(date: any) {
        searchExpirationTime.value = date
    }

    function SET_PREPARE_BOOK_REQUEST(_prepareBookRequest: any) {
        prepareBookRequest.value = _prepareBookRequest
    }

    function SET_PREPARE_BOOK_REQUEST_PROP(data: any) {
        prepareBookRequest.value[data.prop] = data.val
    }

    function SET_BOOKING_ADDITIONAL_OPTIONS(options: any) {
        if (Object.keys(options).length) {
            bookingAdditionalOptions.value = options
        }
    }

    function clearBookingAdditionalOptions() {
        bookingAdditionalOptions.value = []
    }

    function SET_PICK_UP_LOCATION(val: any) {
        pickUpLocation.value = val
    }

    function SET_DROP_OFF_LOCATION(val: any) {
        dropOffLocation.value = val
    }

    async function getExtraServiceAdditionalOptions(offerKey: any) {
        try {
            const res = await appInstance.$api.additionalCarRentOptions.get({offerKey})
            SET_BOOKING_ADDITIONAL_OPTIONS(res)
        } catch (error) {
            console.error(error)
        }
    }

    async function prepareBook(req: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            SET_PREPARE_BOOK_REQUEST(req)
            const response = await appInstance.$api.prepareCarBook.post(req)
            SET_PREPARE_BOOK_RESPONSE(response)
            return response
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    const isOffersExpired = computed(() => {
        return () =>
            hasOffers.value && !carsRentRuntimeStore.searchActive && isAfter(new Date(), searchExpirationTime.value)
    })

    const basketItem = computed(() => {
        return (offerKey: any) => getBasketItem(basket, offerKey)
    })

    const hasOffers = computed(() => {
        return !!searchResponse.value.products.length
    })

    return {
        ...useProductStore(searchResponse),
        searchRequest,
        searchResponse,
        filters,
        sortFnName,
        searchExpirationTime,
        prepareBookRequest,
        prepareBookResponse,
        basket,
        bookingAdditionalOptions,
        pickUpLocation,
        dropOffLocation,
        ADD_TO_BASKET,
        REFRESH_BASKET_PRICE,
        SET_SEARCH_REQUEST,
        RESET_FILTERS,
        SET_FILTER,
        SET_FILTERS,
        SET_SORT,
        NEW_SEARCH,
        RESET,
        SET_PREPARE_BOOK_RESPONSE,
        SET_SEARCH_EXPIRATION_TIME,
        SET_PREPARE_BOOK_REQUEST,
        SET_PREPARE_BOOK_REQUEST_PROP,
        SET_BOOKING_ADDITIONAL_OPTIONS,
        clearBookingAdditionalOptions,
        SET_PICK_UP_LOCATION,
        SET_DROP_OFF_LOCATION,
        getExtraServiceAdditionalOptions,
        prepareBook,
        isOffersExpired,
        basketItem,
        hasOffers,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useCarsRentStore, import.meta.hot))
